import React from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { PLAN_DEFINITIONS } from '@/api-types';
import type { PlanTier } from '@/api-types';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

interface PlanChangeDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	orgId: string;
	currentTier: PlanTier;
	targetTier: PlanTier;
	onSuccess: () => void;
}

export function PlanChangeDialog({
	open,
	onOpenChange,
	orgId,
	currentTier,
	targetTier,
	onSuccess,
}: PlanChangeDialogProps) {
	const [loading, setLoading] = React.useState(false);
	const targetPlan = PLAN_DEFINITIONS[targetTier];
	const isUpgrade = getPlanRank(targetTier) > getPlanRank(currentTier);

	const handleConfirm = async () => {
		setLoading(true);
		try {
			if (isUpgrade && targetPlan.priceCents && targetPlan.priceCents > 0) {
				// Upgrade via Stripe Checkout
				const res = await apiClient.createCheckoutSession(orgId, targetPlan.id);
				if (res.success && res.data?.url) {
					window.location.href = res.data.url;
					return;
				}
				toast.error('Failed to start checkout');
			} else {
				// Downgrade or change via API
				const res = await apiClient.changePlan(orgId, targetPlan.id);
				if (res.success) {
					toast.success(`Plan changed to ${targetPlan.name}`);
					onSuccess();
					onOpenChange(false);
				} else {
					toast.error('Failed to change plan');
				}
			}
		} catch {
			toast.error('Failed to change plan');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{isUpgrade ? 'Upgrade' : 'Change'} to {targetPlan.name}
					</DialogTitle>
					<DialogDescription>
						{isUpgrade
							? 'You will be redirected to Stripe to complete payment.'
							: `Your plan will change to ${targetPlan.name} immediately. Your billing will be prorated.`}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-3 py-4">
					<p className="text-sm font-medium">Included in {targetPlan.name}:</p>
					<ul className="space-y-1">
						{targetPlan.features.slice(0, 5).map((f) => (
							<li key={f} className="flex items-center gap-2 text-sm">
								<Check className="h-4 w-4 text-green-500" />
								{f}
							</li>
						))}
					</ul>
					{targetPlan.priceCents !== null && targetPlan.priceCents > 0 && (
						<p className="text-sm text-text-secondary mt-2">
							${(targetPlan.priceCents / 100).toFixed(0)}/{targetPlan.isPerSeat ? 'seat/' : ''}month
						</p>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
						Cancel
					</Button>
					<Button onClick={handleConfirm} disabled={loading}>
						{loading ? 'Processing...' : isUpgrade ? 'Continue to Checkout' : 'Confirm Change'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function getPlanRank(tier: PlanTier): number {
	const ranks: Record<PlanTier, number> = { free: 0, pro: 1, team: 2, enterprise: 3 };
	return ranks[tier];
}
